import { Weight } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { FaShareAlt, FaFacebookF, FaInstagram, FaTwitter, FaFilePdf, FaMapMarkerAlt, FaBed, FaInfoCircle, FaArrowRight, FaHandPointUp } from "react-icons/fa";
import { GrLocation } from 'react-icons/gr';
import { MdExpandMore } from "react-icons/md";
import { PiBedLight } from "react-icons/pi";
import { FaProcedures } from "react-icons/fa";


// --- API Endpoints ---
const NEW_API_URL = 'https://mithahomesfrontend.pemixcels.com/api/mainprojectprocess/maincard';
const IMG_BASE_URL = 'https://mithahomesfrontend.pemixcels.com';

// --- Shared Styles and Icon Data ---
const COLORS = {
    primaryOrange: '#e46324',
    primaryGreen: '#5ec250', 
    primaryDarkBlue: '#016563',
    textOrange: '#e46d3e',
    cardBg: '#ffffff',
    borderColor: '#e0e0e0',
    lightGray: '#f0f0f0',
    mediumGray: '#888',
    darkGray: '#333',
    mediumDarkGray: '#555',
    shareIconColor: '#444'
};

const socialColors = {
    facebook: '#3b5998',
    instagram: '#C13584',
    twitter: '#1DA1F2',
};

// Category order mapping
const CATEGORY_ORDER = {
    'Ongoing Projects': 1,
    'Ready to Occupy': 2,
    'Completed Projects': 3
};

// --- Static Style Definitions (Passed to CardWithShare) ---
const styles = {
    mainContainerStyle: {
        fontFamily: '"Roboto", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", "Noto Sans", "Liberation Sans", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
        backgroundColor: COLORS.lightGray,
        padding: '20px',
        margin: 0,
        minHeight: '100vh',
    },
    gridStyle: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '20px', 
        justifyContent: 'flex-start',
        maxWidth: '1200px', 
        margin: '0 auto',
    },
    cardContainerStyle: {
        position: 'relative', 
        textDecoration: 'none', 
        color: 'inherit',
        cursor: 'pointer',
        width: 'calc((100% / 3) - 14px)',
        minWidth: '380px',
        flexGrow: 0,
        flexShrink: 0,
        backgroundColor: COLORS.cardBg,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden',
        transition: 'transform 0.3s, box-shadow 0.3s',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: '"Roboto", sans-serif',
    },
    titleStyle: { 
        color: '#333',
        fontSize: '18px', 
        margin: '5px 15px 0', 
        fontWeight: 700,
        textAlign: 'center',
        paddingTop: '20px',
        flexShrink: 0,
        transition: 'color 0.3s ease',
        fontFamily: '"Roboto", sans-serif',
    },
    locationStyle: {
        fontSize: "1rem",
        fontWeight: 400,
        padding: '5px',
        color: "#777",
        textAlign: 'center',
        margin: '0 15px',
        transition: 'color 0.3s ease',
        fontFamily: '"Roboto", sans-serif',
    },
    imageContainerStyle: { 
        position: 'relative', 
        width: '100%', 
        flex: '1 0 auto',
        backgroundColor: '#eee',
        overflow: 'hidden',
    },
    promoImageStyle: { 
        width: '100%', 
        height: '100%', 
        objectFit: 'cover',
        display: 'block',
    },
    detailsSectionStyle: { 
        padding: '15px', 
        zIndex: 90,
        borderBottom: `1px solid ${COLORS.borderColor}`,
        flexShrink: 0,
        fontFamily: '"Roboto", sans-serif',
    },
    detailsTitleStyle: { 
        fontSize: '1.2em', 
        margin: '0 0 15px', 
        fontWeight: 700, 
        color: COLORS.darkGray,
        fontFamily: '"Roboto", sans-serif',
    },
    detailItemStyle: { 
        display: 'flex', 
        alignItems: 'flex-start', 
        marginBottom: '10px', 
        marginLeft: '15px',
        gap: '10px',
        fontFamily: '"Roboto", sans-serif',
    },
    detailPStyle: { 
        margin: "1px", 
        fontSize: '15px', 
        lineHeight: 1.3, 
        color: COLORS.mediumDarkGray,
        fontWeight: 400,
        fontFamily: '"Roboto", sans-serif',
    },
    detailIconStyle: { 
        color: "#af9c78",
        fontSize: '30px',
        flexShrink: 0, 
        marginTop: '2px',
    },
    actionsSectionStyle: { 
        display: 'flex', 
        justifyContent: 'space-between', 
        padding: '15px', 
        gap: '10px',
        flexShrink: 0,
    },
    btnBaseStyle: { 
        flexGrow: 1, 
        display: 'flex', 
        borderRadius:'5px',
        alignItems: 'center', 
        justifyContent: 'center', 
        padding: '8px', 
        textDecoration: 'none', 
        color: 'white', 
        fontWeight: 500, 
        fontSize: '0.9em', 
        whiteSpace: 'nowrap', 
        transition: 'background-color 0.2s',
        fontFamily: '"Roboto", sans-serif',
    },
    viewMoreBtnStyle: { backgroundColor: COLORS.primaryOrange },
    brochureBtnStyle: { backgroundColor: COLORS.primaryGreen },
    getDirectionBtnStyle: { backgroundColor: COLORS.primaryDarkBlue },
    btnIconStyle: { 
        marginRight: '5px',
        fontSize: '16px',
    },
    // --- Share Icon Styles ---
    verticalShareContainerStyle: { 
        position: 'absolute', 
        top: '10px', 
        left: '10px', 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        zIndex: 11,
    },
    mainShareButtonStyle: { 
        width: '45px', 
        height: '45px', 
        backgroundColor: 'rgba(255, 255, 255, 0.9)', 
        borderRadius: '50%',
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        zIndex: 15,
        cursor: 'pointer',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        marginBottom: '5px',
        border: 'none',
    },
    mainShareIconStyle: { 
        fontSize: '1.2em', 
        color: COLORS.shareIconColor 
    },
    socialIconStyle: (color) => ({ 
        width: '30px', 
        height: '30px', 
        borderRadius: '50%',
        backgroundColor: color, 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        fontSize: '0.9em',
        color: 'white',
        cursor: 'pointer',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        marginBottom: '5px',
        border: 'none',
    }),
};

// Add Roboto font import in your HTML head or create a CSS file
// Alternatively, add this in your main App component or index file:
// <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap" rel="stylesheet" />

const CardWithShare = ({ project, projectDetailsUrl }) => {
    
    const [showSocialIcons, setShowSocialIcons] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    
    const handleActionClick = (e, action) => {
        e.stopPropagation();
        if (action === 'share') {
            e.preventDefault(); 
            alert(`Sharing to ${e.currentTarget.getAttribute('data-social')} for ${project.project_name}`);
        }
    }
    
    const viewMoreBtnStyle = { ...styles.btnBaseStyle, ...styles.viewMoreBtnStyle };
    const brochureBtnStyle = { ...styles.btnBaseStyle, ...styles.brochureBtnStyle };
    const getDirectionBtnStyle = { ...styles.btnBaseStyle, ...styles.getDirectionBtnStyle };

    // Handle image error
    const handleImageError = (e) => {
        e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Available';
    };

    // Dynamic styles for title and location based on hover state
    const titleDynamicStyle = {
        ...styles.titleStyle,
        color: isHovered ? COLORS.textOrange : '#333'
    };

    const locationDynamicStyle = {
        ...styles.locationStyle,
        color: isHovered ? COLORS.textOrange : '#777'
    };

    return (
        <>
           <a 
            href={projectDetailsUrl} 
            style={styles.cardContainerStyle}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
        
          <div style={{margin:'5px',justifyContent:'center', textAlign:'center'}}>
              <h4 style={titleDynamicStyle}>{project.project_name || "Project Name Missing"}</h4>
            <h6 style={locationDynamicStyle}>
                {project.location || "Location Missing"}
            </h6>  
            </div>      
            
            <div style={styles.imageContainerStyle}>
                <img 
                    src={IMG_BASE_URL + (project.banner_image || '')} 
                    alt={project.project_name} 
                    style={styles.promoImageStyle}
                    onError={handleImageError}
                    loading="lazy"
                />
         
            </div>

            {/* 3. Details Section - Fixed font weight */}
            <div style={styles.detailsSectionStyle}>
                <h2 style={styles.detailsTitleStyle}>{project.project_name || "Project Details"}</h2>
                <div style={styles.detailItemStyle}>
                    <span style={styles.detailIconStyle}><GrLocation /></span>
                    <p style={styles.detailPStyle}><strong style={{fontWeight:420, fontFamily: '"Roboto", sans-serif'}}>{project.location || "Location data unavailable"}</strong></p>
                </div>
                <div style={styles.detailItemStyle}>
                    <span style={styles.detailIconStyle}><PiBedLight /></span>
                    <p style={styles.detailPStyle}><strong style={{fontWeight: 420, fontFamily: '"Roboto", sans-serif'}}>{project.project_caption || "Stage data unavailable"}</strong></p>
                </div>
                <div style={styles.detailItemStyle}>
                    <span style={styles.detailIconStyle}><FaProcedures /></span>
                    <p style={styles.detailPStyle}>
                        <strong style={{fontWeight: 420, fontFamily: '"Roboto", sans-serif'}}>{project.category_id || '...'}</strong>
                    </p>
                </div>
            </div>
            <div style={styles.actionsSectionStyle}>
                <a href={projectDetailsUrl} style={viewMoreBtnStyle} onClick={(e) => e.stopPropagation()}>
                    <span style={styles.btnIconStyle}><FaHandPointUp /></span>View More
                </a>
                <a href={project.attached_brochure || '#'} style={brochureBtnStyle} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                    <span style={styles.btnIconStyle}><FaFilePdf /></span>Brochure
                </a>
                <a href={project.get_direction || '#'} style={getDirectionBtnStyle} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                    <span style={styles.btnIconStyle}><FaArrowRight /></span>Get Direction
                </a>
            </div>
        </a>
        </>
     
    );
};


// --- Main Grid Component ---
const ProjectCardsGrid = () => {
    const [projects, setProjects] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // --- Api Calls ---
    useEffect(() => {
        const fetchAllProjects = async () => {
            try {
                const response = await fetch(NEW_API_URL);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const result = await response.json();
                
                if (result.success && result.data && Array.isArray(result.data)) {
                    // Sort projects by category order
                    const sortedProjects = result.data.sort((a, b) => {
                        const orderA = CATEGORY_ORDER[a.category_id] || 99;
                        const orderB = CATEGORY_ORDER[b.category_id] || 99;
                        return orderA - orderB;
                    });
                    setProjects(sortedProjects); 
                } else {
                    if (result.success && result.data && result.data.length === 0) {
                        setProjects([]);
                    } else {
                        setError("Invalid data format received from the API or API reported failure.");
                    }
                }
            } catch (e) {
                console.error("Fetching error: ", e);
                setError(`Failed to fetch project details. Check network connection or API URL.`);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAllProjects();
    }, []);

    // Add Roboto font to your HTML head or main App component
    useEffect(() => {
        // Dynamically add Roboto font
        const link = document.createElement('link');
        link.href = 'https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap';
        link.rel = 'stylesheet';
        document.head.appendChild(link);
        
        return () => {
            document.head.removeChild(link);
        };
    }, []);
  
    return (
        <>
        <div style={styles.mainContainerStyle}>
      
            
            {isLoading ? (
<>
</>            ) : error ? (
                <div style={{textAlign: 'center', padding: '50px', color: 'red'}}>{error}</div>
            ) : projects.length === 0 ? (
                <div style={{textAlign: 'center', padding: '50px'}}>No projects found.</div>
            ) : (
                <div style={styles.gridStyle}>
                    {projects.map((project, index) => { 
                        const projectDetailsUrl = project?.pid ? `/project/${project.pid}` : '#';
                        const key = project.pid || index; 
                        
                        return (
                            <CardWithShare 
                                key={key} 
                                project={project} 
                                projectDetailsUrl={projectDetailsUrl}
                            />
                        );
                    })}
                </div>
            )}
        </div>
        </>
    );
};

export default ProjectCardsGrid;